import { useEffect, useRef, useState } from 'react';

interface VoiceRecorderProps {
  onComplete: (file: File) => void;
  onCancel: () => void;
}

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

function pickMimeType(): string {
  for (const m of MIME_CANDIDATES) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return 'audio/webm';
}

export function VoiceRecorder({ onComplete, onCancel }: VoiceRecorderProps) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeRef = useRef<string>('audio/webm');
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mime = pickMimeType();
        mimeRef.current = mime;
        const rec = new MediaRecorder(stream, { mimeType: mime });
        recorderRef.current = rec;
        chunksRef.current = [];

        rec.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };

        rec.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: mime });
          const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
          const file = new File(
            [blob],
            `voice-message-${Date.now()}.${ext}`,
            { type: mime },
          );
          stream.getTracks().forEach((t) => t.stop());
          if (file.size > 0) onComplete(file);
          else onCancel();
        };

        rec.start();
        setRecording(true);

        tickRef.current = window.setInterval(() => {
          setSeconds((s) => s + 1);
        }, 1000);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('mic access failed', err);
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          setError('Microphone access denied.');
        } else {
          setError('Could not start recording.');
        }
      }
    })();

    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      // If component unmounts mid-recording without stop being called.
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        recorderRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stop() {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    if (tickRef.current) window.clearInterval(tickRef.current);
    setRecording(false);
  }

  function cancel() {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      // Drop chunks before stopping so onstop produces an empty file → onCancel.
      chunksRef.current = [];
      recorderRef.current.stop();
    }
    if (tickRef.current) window.clearInterval(tickRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    onCancel();
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 bg-rose-500/10 border border-rose-500/30 rounded-xl px-3 py-2">
        <span className="text-xs text-rose-300">{error}</span>
        <button
          onClick={onCancel}
          className="ml-auto text-xs text-zinc-400 hover:text-zinc-100 transition"
        >
          Close
        </button>
      </div>
    );
  }

  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toString().padStart(2, '0');

  return (
    <div className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2">
      <span className="flex items-center gap-2 text-rose-400">
        <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
        <span className="text-xs font-medium">Recording</span>
      </span>
      <span className="text-xs font-mono text-zinc-300 tabular-nums">
        {mins}:{secs}
      </span>
      <span className="flex-1" />
      <button
        onClick={cancel}
        className="text-xs text-zinc-400 hover:text-zinc-100 transition"
      >
        Cancel
      </button>
      <button
        onClick={stop}
        disabled={!recording}
        className="rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 px-3 py-1 text-xs font-medium text-white transition"
      >
        Send
      </button>
    </div>
  );
}
